import React, { useContext, useLayoutEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Paper,
  Switch,
} from "@mui/material";
import NotificationsOffOutlinedIcon from "@mui/icons-material/NotificationsOffOutlined";
import { useTranslation } from "react-i18next";
import AuthContext from "../../../contexts/AuthContext";
import { setSmsEnabled } from "../../../pages/Chat/smsConsent";
import { toast } from "../../../utilities/SnackBarUtils";
import FavoriteIcon from "@mui/icons-material/Favorite";
import TuneIcon from "@mui/icons-material/Tune";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";

const GuestListTutorial = ({
  step,
  anchorRef,
  onNext,
  onFinish,
  onChatDone,
  onSmsDone,
}) => {
  const { t } = useTranslation();
  const { appConfig } = useContext(AuthContext);
  const [rect, setRect] = useState(null);
  const [smsOn, setSmsOn] = useState(false);
  const [smsSaving, setSmsSaving] = useState(false);

  const toggleSms = async () => {
    const next = !smsOn;
    setSmsOn(next);
    setSmsSaving(true);
    const result = await setSmsEnabled(next);
    setSmsSaving(false);
    if (!result?.success) {
      setSmsOn(!next);
      toast.error(result?.error || t("settings.sms.error"));
    }
  };

  useLayoutEffect(() => {
    if (step !== "filter") return;

    const measure = () => {
      const el = anchorRef?.current;
      if (el) setRect(el.getBoundingClientRect());
    };

    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [step, anchorRef]);

  if (step === "welcome") {
    return (
      <Dialog
        open
        fullWidth
        maxWidth="xs"
        onClose={onNext}
        PaperProps={{ sx: { borderRadius: "24px" } }}
      >
        <DialogTitle sx={{ textAlign: "center", pt: 3, fontWeight: 700 }}>
          Welcome to the Guest List
        </DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Typography variant="body1">
              This is everyone checked in for tonight&apos;s event. Each card is
              a guest you could meet.
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  flexShrink: 0,
                  px: 0.8,
                  py: 0.3,
                  borderRadius: 5,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                96%
              </Box>
              <Typography variant="body2">
                The <b>match score</b> in the corner shows how well your event
                profile answers line up with theirs.
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <FavoriteIcon sx={{ color: "#68C371", flexShrink: 0 }} />
              <Typography variant="body2">
                Tap a guest to see more, then hit the <b>heart</b> to like them.
                Anyone you like turns green.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pt: 1, pb: 3 }}>
          <Button
            className="tnfm-pill-btn"
            onClick={onNext}
            variant="contained"
            fullWidth
          >
            Next
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (step === "filter" && rect) {
    const pad = 6;

    return (
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: (t) => t.zIndex.modal + 1,
        }}
      >
        <Box
          onClick={onFinish}
          sx={{ position: "fixed", inset: 0, cursor: "pointer" }}
        />

        <Box
          sx={{
            position: "fixed",
            top: rect.top - pad,
            left: rect.left - pad,
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
            borderRadius: "50%",
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.6)",
            pointerEvents: "none",
          }}
        />

        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            top: rect.bottom + 14,
            right: 12,
            maxWidth: 260,
            p: 2.5,
            borderRadius: "24px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <TuneIcon color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Filter the list
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Tap here to narrow the guest list down to the things that matter most
            to you — interests, values, lifestyle and more.
          </Typography>
          <Button
            className="tnfm-pill-btn"
            onClick={onFinish}
            variant="contained"
            fullWidth
          >
            Got it
          </Button>
        </Paper>
      </Box>
    );
  }

  if (step === "chat") {
    return (
      <Dialog
        open
        fullWidth
        maxWidth="xs"
        onClose={onChatDone}
        PaperProps={{ sx: { borderRadius: "24px" } }}
      >
        <DialogTitle sx={{ textAlign: "center", pt: 3, fontWeight: 700 }}>
          Hearts open chats tonight
        </DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <FavoriteIcon sx={{ color: "#68C371", flexShrink: 0 }} />
              <Typography variant="body2">
                <b>Heart</b> anyone you would like to talk to. They will not
                know unless they heart you too.
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <ChatBubbleIcon color="primary" sx={{ flexShrink: 0 }} />
              <Typography variant="body2">
                If they <b>heart you back</b>, you will both be notified and a
                private chat opens between the two of you.
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary">
              Find your chats under <b>Chats</b> in the menu bar. They close when
              the event ends.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pt: 1, pb: 3 }}>
          <Button
            className="tnfm-pill-btn"
            onClick={onChatDone}
            variant="contained"
            fullWidth
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (step === "sms") {
    return (
      <Dialog
        open
        fullWidth
        maxWidth="xs"
        onClose={onSmsDone}
        PaperProps={{ sx: { borderRadius: "24px" } }}
      >
        <DialogTitle sx={{ textAlign: "center", pt: 3, fontWeight: 700 }}>
          {t("chat.sms.title")}
        </DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <NotificationsOffOutlinedIcon
                color="primary"
                sx={{ flexShrink: 0 }}
              />
              <Typography variant="body2">{t("chat.sms.body")}</Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: "16px",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("chat.sms.toggle")}
              </Typography>
              <Switch
                checked={smsOn}
                disabled={smsSaving}
                onChange={toggleSms}
                inputProps={{ "aria-label": t("chat.sms.toggle") }}
              />
            </Box>

            <Typography variant="caption" color="text.secondary">
              {t("auth.sms.checkbox.prefix")} {appConfig?.orgName}{" "}
              {t("auth.sms.checkbox.description")}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pt: 1, pb: 3 }}>
          <Button
            className="tnfm-pill-btn"
            onClick={onSmsDone}
            variant="contained"
            fullWidth
            disabled={smsSaving}
          >
            {t("chat.sms.done")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return null;
};

export default GuestListTutorial;
